import type { ReactNode } from 'react'
import { Navigate, Outlet, useParams } from 'react-router'
import { paths } from '@/app/router/paths'
import { useSessionStore } from '../store/sessionStore'
import type { WizardStep } from '../types/session.types'
import { checkFaceVerified, checkSession, checkStepOrder, checkSubmittedAccess } from './guardRules'
import { stepPath } from './stepPath'

/**
 * Guards are thin: every decision comes from the pure functions in guardRules.ts,
 * which are unit-tested exhaustively. These components only translate a decision
 * into a `<Navigate>`.
 *
 * Two rules hold for all of them, and both are load-bearing rather than stylistic.
 *
 * SUBSCRIBE WITH A SELECTOR, never `useSessionStore()` bare. The whole-store form
 * (and destructuring it, which does not narrow) re-renders the guard on every `set`
 * anywhere in the app — and a guard that is mid-redirect re-renders into another
 * redirect.
 *
 * PASS A STABLE `state` OBJECT to `<Navigate>`. react-router lists the raw object in
 * its effect's dependency array (`[navigate, jsonPath, relative, replace, state]`),
 * so a `state={{ ... }}` literal is a new identity every render and re-issues the
 * navigation every render. Hence the frozen constants below rather than an inline
 * object.
 */
const REDIRECT_STATE = {
  no_session: Object.freeze({ reason: 'no_session' }),
  ref_mismatch: Object.freeze({ reason: 'ref_mismatch' }),
} as const

export function RequireSession({ children }: { children?: ReactNode }) {
  const { appRefNo: paramRef } = useParams<{ appRefNo: string }>()
  const appRefNo = useSessionStore((s) => s.appRefNo)
  const decision = checkSession({ paramRef, state: { appRefNo } })

  if (!decision.allow && decision.redirectTo === 'start') {
    return <Navigate to={paths.apply.root} replace state={REDIRECT_STATE[decision.reason]} />
  }

  return children ?? <Outlet />
}

export function RequireFaceVerified({ children }: { children?: ReactNode }) {
  const { appRefNo } = useParams<{ appRefNo: string }>()
  const faceVerified = useSessionStore((s) => s.faceVerified)
  const faceBlocked = useSessionStore((s) => s.faceBlocked)
  const decision = checkFaceVerified({ faceVerified, faceBlocked })

  if (!decision.allow && appRefNo) {
    const to =
      decision.redirectTo === 'blocked'
        ? paths.apply.unableToVerify(appRefNo)
        : paths.apply.verify(appRefNo)
    return <Navigate to={to} replace />
  }

  return children ?? <Outlet />
}

export function RequireStep({ step, children }: { step: WizardStep; children?: ReactNode }) {
  const { appRefNo } = useParams<{ appRefNo: string }>()
  const completedSteps = useSessionStore((s) => s.completedSteps)
  const decision = checkStepOrder(step, completedSteps)

  if (!decision.allow && decision.redirectTo === 'step' && appRefNo) {
    return <Navigate to={stepPath(appRefNo, decision.step)} replace />
  }

  return children ?? <Outlet />
}

/**
 * Keeps an unsigned application off the receipt.
 *
 * The receipt is not a passive screen: it fires §4.10 finalise on mount, and
 * §4.10 has no idempotency key (docs/api-gaps.md #8). Without this, a citizen who
 * had passed face verification could deep-link past the declaration and submit.
 *
 * Deliberately NOT `RequireStep step="declaration"` — see checkSubmittedAccess.
 *
 * Safe across a refresh of the receipt itself: `clearApplicantData()` keeps the
 * completion flags precisely so the guards do not eject a citizen from the screen
 * they just reached.
 *
 * Client-side only, and therefore UX rather than a control — completedSteps is
 * hand-editable in devtools. Step order must also be enforced server-side, the
 * same caveat RequireNotBlocked carries below.
 */
export function RequireDeclarationSigned({ children }: { children?: ReactNode }) {
  const { appRefNo } = useParams<{ appRefNo: string }>()
  const completedSteps = useSessionStore((s) => s.completedSteps)
  const decision = checkSubmittedAccess(completedSteps)

  if (!decision.allow && decision.redirectTo === 'step' && appRefNo) {
    return <Navigate to={stepPath(appRefNo, decision.step)} replace />
  }

  return children ?? <Outlet />
}

/**
 * Keeps a blocked applicant out of the camera screen.
 *
 * The verify route cannot sit inside RequireFaceVerified — that guard exists to
 * send people *to* it — which left the back button as an escape from the terminal
 * state after three failed attempts. This closes it.
 *
 * Client-side only, and therefore UX rather than a control: the attempt limit must
 * also be enforced server-side. See docs/api-gaps.md #5.
 */
export function RequireNotBlocked({ children }: { children?: ReactNode }) {
  const { appRefNo } = useParams<{ appRefNo: string }>()
  const faceBlocked = useSessionStore((s) => s.faceBlocked)

  if (faceBlocked && appRefNo) {
    return <Navigate to={paths.apply.unableToVerify(appRefNo)} replace />
  }

  return children ?? <Outlet />
}
